import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Typography } from "@mui/material"
import { Controller, useForm } from "react-hook-form"
import { TFechamentos } from "../../../../../../types/api/financeiro/pesquisar-fechamentos"
import { Moment } from "moment"
import SelectConfigFechamentos from "../../../../../../components/SelectsSimpleApiQuery/SelectConfigFechamentos"
import BasicDatePicker from "../../../../../../components/BasicDatePicker"
import DateComIntervalo from "../../../../../../components/DateComIntervalo"
import MultipleDatePicker from "../../../../../../components/MultipleDataPicker"
import { useMutation, useQueryClient } from "react-query"
import axiosClient from "../../../../../../lib/axios"
import { enqueueSnackbar } from "notistack"
import { IMutationAddFechamento } from "../../../../../../types/mutations/financeiro/adicionar-fechamento"

export default function DialogAddFechamento({
    open,
    handleClose,
    handleSucessAddFechamento
}: DialogAddFechamentoProps) {

    const queryClient = useQueryClient();

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        formState: { errors, isValid }
    } = useForm<FormInputs>({
        defaultValues: {
            fechamento: null,
            data_fechamento: null,
            data_emissao: null,
            data_apuracao: {
                inicial: null,
                final: null
            },
            vencimento_tit_pagar: [],
            vencimento_tit_receber: []
        },
        mode: "onChange"
    })

    const { mutate: adicionarFechamento, isLoading } = useMutation({
        mutationKey: 'adicionar-fechamento',
        mutationFn: (inputs: IMutationAddFechamento) => axiosClient.post(`/financeiro/conta-corrente-consumo/adicionar-fechamento`, inputs),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar("Erro ao adicionar fechamento", { variant: "error" })
        },
        onSuccess: (data) => {
            enqueueSnackbar("Fechamento adicionado com sucesso", { variant: "success" })
            queryClient.invalidateQueries([`pesquisar-proximos-fechamentos`])
            reset()
            handleSucessAddFechamento(data.data)
        },
    });

    const onSubmit = (data: FormInputs) => {
        const params: IMutationAddFechamento = {
            configuracao_id: data.fechamento?.id || 0,
            data_fechamento: data.data_fechamento?.format("YYYY-MM-DD") || '',
            data_emissao: data.data_emissao?.format("YYYY-MM-DD") || '',
            data_apuracao_inicio: data.data_apuracao.inicial?.format("YYYY-MM-DD") || '',
            data_apuracao_fim: data.data_apuracao.final?.format("YYYY-MM-DD") || '',
            vencimento_tit_pagar: data.vencimento_tit_pagar.map(item => item.format("YYYY-MM-DD")),
            vencimento_tit_receber: data.vencimento_tit_receber.map(item => item.format("YYYY-MM-DD"))
        }

        adicionarFechamento(params)
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            scroll="body"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    Adicionar fechamento
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2} my={0.2}>
                        <Grid size={{xs: 12}}>
                            <Controller
                                control={control}
                                name="fechamento"
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field: { onChange, value } }) => (
                                    <SelectConfigFechamentos
                                        value={value}
                                        onChange={onChange}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{xs: 12, md: 6}}>
                            <Controller
                                control={control}
                                name="data_fechamento"
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field: { onChange, value } }) => (
                                    <BasicDatePicker
                                        label="Data de fechamento"
                                        value={value}
                                        onChange={(newValue) => {
                                            onChange(newValue);
                                            const currentEmissao = control._getWatch('data_emissao');
                                            if (!currentEmissao && newValue) {
                                                setValue('data_emissao', newValue);
                                            }
                                        }}
                                        formatString="DD/MM/YYYY"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{xs: 12, md: 6}}>
                            <Controller
                                control={control}
                                name="data_emissao"
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field: { onChange, value } }) => (
                                    <BasicDatePicker
                                        label="Data de emissão"
                                        value={value}
                                        onChange={onChange}
                                        formatString="DD/MM/YYYY"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <Controller
                                control={control}
                                name="data_apuracao"
                                rules={{
                                    validate: (value) => {
                                        return (value.inicial && value.final) ? true : "Período de apuração é obrigatório"
                                    }
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <DateComIntervalo
                                        data={value}
                                        labelInput="Período de apuração"
                                        onChangeData={onChange}
                                        formatString="DD/MM/YYYY"
                                        datePickerView={['year', 'month', 'day']}
                                        error={!!errors.data_apuracao}
                                        helperText={errors.data_apuracao?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <Divider />
                            <Typography
                                mt={2}
                                variant="subtitle1"
                                fontWeight="bold"
                            >
                                Vencimento dos títulos a serem gerados
                            </Typography>
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <Controller
                                control={control}
                                name="vencimento_tit_pagar"
                                rules={{
                                    validate: (value) => {
                                        return value.length > 0 || "Adicione pelo menos uma data de vencimento"
                                    }
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <MultipleDatePicker
                                        blockPastDates
                                        label="Títulos a pagar"
                                        value={value}
                                        formatString="DD/MM/YYYY"
                                        onChange={onChange}
                                        error={!!errors.vencimento_tit_pagar}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <Controller
                                control={control}
                                name="vencimento_tit_receber"
                                rules={{
                                    validate: (value) => {
                                        return value.length > 0 || "Adicione pelo menos uma data de vencimento"
                                    }
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <MultipleDatePicker
                                        blockPastDates
                                        label="Títulos a receber"
                                        value={value}
                                        formatString="DD/MM/YYYY"
                                        onChange={onChange}
                                        error={!!errors.vencimento_tit_receber}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleClose}
                        type="button"
                    >
                        Cancelar
                    </Button>

                    <Button
                        disabled={!isValid || isLoading}
                        autoFocus
                        variant="contained"
                        type="submit"
                    >
                        {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        Adicionar
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}

interface DialogAddFechamentoProps {
    open: boolean,
    handleClose: () => void
    handleSucessAddFechamento: (id: number) => void
}

interface FormInputs {
    fechamento: TFechamentos | null
    data_fechamento: Moment | null,
    data_emissao: Moment | null,
    data_apuracao: {
        inicial: Moment | null,
        final: Moment | null
    }
    vencimento_tit_pagar: Moment[],
    vencimento_tit_receber: Moment[]
}

